import { useMemo, useReducer, useState } from 'react';
import { Clock, Leaf, Plus, ShoppingBag } from 'lucide-react';
import { MAX_REQUEST_NOTE_LENGTH } from '@hotel-voice/shared';
import { AppShell } from '@/components/layout/AppShell';
import { PageHeader } from '@/components/layout/PageHeader';
import { QuantityStepper } from '@/components/common/QuantityStepper';
import { StorageImage } from '@/components/common/StorageImage';
import { EmptyState, ErrorState, ListSkeleton, SuccessState, UnavailableState } from '@/components/common/StateView';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useReadyGuest } from '@/contexts/GuestSessionContext';
import { useSubmitRequest } from '@/hooks/useSubmitRequest';
import { formatCurrency } from '@/lib/money';
import { cn } from '@/lib/utils';
import type { MenuItem } from '@/types';
import { EMPTY_CART, cartCount, cartLines, cartReducer, cartTotal, groupByCategory, toRequestItems } from './cart';
import { OrderSheet } from './OrderSheet';
import { RESTAURANT_CATALOG, STORE_CATALOG, type OrderCatalog } from './catalog';

const ALL = '__all__';

/**
 * The restaurant menu, category by category, with a cart that lives at the
 * foot of the screen.
 *
 * Every dish row has an Add button that turns into a stepper once the dish is
 * in the cart. The order is reviewed in a sheet (item, qty, price, total, a
 * note for the chef) before "Place Order" — which is guarded twice against a
 * double tap: the button disables on the first tap, and the request carries
 * an idempotency key so even a retry cannot create a second order.
 */
export function RestaurantPage({ catalog = RESTAURANT_CATALOG }: { catalog?: OrderCatalog }) {
  const { session, services, servicesPhase, servicesError, reloadServices } = useReadyGuest();
  const { submitting, result, submit, reset } = useSubmitRequest(catalog.type);

  const [cart, dispatch] = useReducer(cartReducer, EMPTY_CART);
  const [filter, setFilter] = useState(ALL);
  const [reviewOpen, setReviewOpen] = useState(false);

  const menu = useMemo(() => (services ? catalog.pick(services).items : []), [services, catalog]);
  const groups = useMemo(
    () => groupByCategory(services ? catalog.pick(services).categories : [], menu),
    [services, catalog, menu],
  );
  const visible = filter === ALL ? groups : groups.filter((g) => g.category === filter);

  const lines = useMemo(() => cartLines(cart, menu), [cart, menu]);
  const total = cartTotal(lines);
  const count = cartCount(lines);

  const placeOrder = async () => {
    if (lines.length === 0 || submitting) return;
    const ok = await submit({ items: toRequestItems(lines), note: cart.note });
    if (ok) {
      setReviewOpen(false);
      dispatch({ type: 'clear' });
    }
  };

  const body = () => {
    if (servicesPhase === 'loading' || servicesPhase === 'idle') return <ListSkeleton rows={6} />;
    if (servicesPhase === 'error') {
      return <ErrorState message={servicesError ?? catalog.loadError} onRetry={reloadServices} />;
    }
    if (!services?.flags[catalog.flag]) return <UnavailableState service={catalog.serviceName} />;

    if (result) {
      return (
        <SuccessState
          title={catalog.successTitle}
          message={`${catalog.placed(formatCurrency(result.totalCharge))} Room ${session.roomNumber}.`}
        >
          <Button onClick={reset}>{catalog.again}</Button>
        </SuccessState>
      );
    }

    if (menu.length === 0) {
      return <EmptyState title={catalog.emptyTitle} message="Please check back later, or call reception." />;
    }

    return (
      <>
        {groups.length > 1 && (
          <Tabs value={filter} onValueChange={setFilter} className="bg-background sticky top-[3.5rem] z-20 px-4 py-2">
            <TabsList className="w-full max-w-full justify-start overflow-x-auto [scrollbar-width:none]">
              <TabsTrigger value={ALL}>All</TabsTrigger>
              {groups.map((group) => (
                <TabsTrigger key={group.category} value={group.category}>
                  {group.category}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        )}

        <div className={cn('flex flex-col gap-5 px-4 pb-4', count > 0 && 'pb-24')}>
          {visible.map((group) => (
            <section key={group.category} aria-labelledby={`cat-${group.category}`}>
              <h2 id={`cat-${group.category}`} className="mb-2 text-base font-semibold">
                {group.category}
              </h2>
              <ul className="flex flex-col gap-2">
                {group.items.map((item) => (
                  <MenuItemRow
                    key={item.id}
                    item={item}
                    quantity={cart.quantities[item.id] ?? 0}
                    onAdd={() => dispatch({ type: 'add', itemId: item.id })}
                    onSet={(quantity) => dispatch({ type: 'set', itemId: item.id, quantity })}
                    disabled={submitting}
                    dishFields={catalog.dishFields}
                  />
                ))}
              </ul>
            </section>
          ))}
        </div>

        {count > 0 && (
          <div className="fixed inset-x-0 bottom-[calc(4rem+env(safe-area-inset-bottom))] z-30 mx-auto w-full max-w-md px-4">
            <Button size="lg" className="w-full justify-between shadow-lg" onClick={() => setReviewOpen(true)}>
              <span className="flex items-center gap-2">
                <ShoppingBag />
                {count} {count === 1 ? 'item' : 'items'}
              </span>
              <span className="tabular-nums">{formatCurrency(total)} · Review order</span>
            </Button>
          </div>
        )}

        <OrderSheet
          open={reviewOpen}
          onOpenChange={setReviewOpen}
          lines={lines}
          total={total}
          note={cart.note}
          onNoteChange={(note) => dispatch({ type: 'note', note: note.slice(0, MAX_REQUEST_NOTE_LENGTH) })}
          onSetQuantity={(itemId, quantity) => dispatch({ type: 'set', itemId, quantity })}
          onRemove={(itemId) => dispatch({ type: 'remove', itemId })}
          onPlaceOrder={placeOrder}
          submitting={submitting}
          noteLabel={catalog.noteLabel}
          notePlaceholder={catalog.notePlaceholder}
        />
      </>
    );
  };

  return (
    <AppShell>
      <PageHeader title={catalog.title} subtitle={catalog.subtitle} />
      {body()}
    </AppShell>
  );
}

/** The hotel store: the same page with the store's catalogue and words. */
export function StorePage() {
  return <RestaurantPage catalog={STORE_CATALOG} />;
}

function MenuItemRow({
  item,
  quantity,
  onAdd,
  onSet,
  disabled,
  dishFields,
}: {
  item: MenuItem;
  quantity: number;
  onAdd: () => void;
  onSet: (quantity: number) => void;
  disabled: boolean;
  dishFields: boolean;
}) {
  const image = item.images?.[0]?.path;
  return (
    <li className={cn('bg-card flex gap-3 rounded-2xl border p-3 shadow-xs', quantity > 0 && 'border-primary/40')}>
      {image && <StorageImage path={image} alt={item.name} className="size-20 shrink-0 rounded-xl" />}
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-start gap-2">
          {dishFields && item.vegetarian !== undefined && (
            <span
              className={cn(
                'mt-1 flex size-3.5 shrink-0 items-center justify-center rounded-[3px] border-[1.5px]',
                item.vegetarian ? 'border-emerald-600' : 'border-red-600',
              )}
              aria-label={item.vegetarian ? 'Vegetarian' : 'Non-vegetarian'}
            >
              <span className={cn('size-1.5 rounded-full', item.vegetarian ? 'bg-emerald-600' : 'bg-red-600')} />
            </span>
          )}
          <p className="min-w-0 flex-1 text-sm font-semibold leading-tight">{item.name}</p>
        </div>
        {item.description && (
          <p className="text-muted-foreground mt-0.5 line-clamp-2 text-xs">{item.description}</p>
        )}
        <div className="mt-auto flex items-center justify-between gap-2 pt-2">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold tabular-nums">{formatCurrency(item.price)}</span>
            {dishFields && item.preparationMinutes && (
              <Badge variant="secondary" className="gap-1 font-medium">
                <Clock /> {item.preparationMinutes} min
              </Badge>
            )}
            {dishFields && item.vegetarian && <Leaf className="size-3.5 text-emerald-600" aria-hidden="true" />}
          </div>
          {quantity > 0 ? (
            <QuantityStepper label={item.name} value={quantity} onChange={onSet} disabled={disabled} />
          ) : (
            <Button size="sm" variant="outline" onClick={onAdd} disabled={disabled} aria-label={`Add ${item.name}`}>
              <Plus /> Add
            </Button>
          )}
        </div>
      </div>
    </li>
  );
}
